import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentPropsWithoutRef, type ElementRef, type ReactNode, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/** Accessible underline tabs (Radix primitives + Ingazi styling) — the bell's
 *  "Needs action / Updates" strip. Radix supplies `tablist` / `tab` / `tabpanel`,
 *  `aria-controls`, arrow-key roving focus and automatic activation; only the
 *  selected panel is mounted. For the pill-style strip see `SegmentedTabs`. */
export const Tabs = TabsPrimitive.Root;

export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List ref={ref} className={cn('flex gap-[18px] border-b px-4', className)} {...props} />
));
TabsList.displayName = 'TabsList';

export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    /** Shown in a pill beside the label ("7", "3 unread"); omit for nothing to report. */
    count?: ReactNode;
    /** Tints the pill for work waiting on the viewer. */
    urgent?: boolean;
  }
>(({ className, children, count, urgent = false, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex min-h-[24px] items-center gap-1.5 py-2.5 text-[13px] font-semibold text-muted-foreground outline-none transition-colors',
      'hover:text-foreground focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring',
      'data-[state=active]:text-foreground data-[state=active]:shadow-[inset_0_-2px_0_hsl(var(--primary))]',
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  >
    {children}
    {count !== undefined && count !== null ? (
      <span
        className={cn(
          'rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums',
          urgent ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary',
        )}
      >
        {count}
      </span>
    ) : null}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', className)}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';
